// ============================================================================
// BonBoZ: phần tính toán thuần của `BoChonMuc.vue` — bộ chọn dùng chung cho
// "mẫu trả lời theo trang" và "tag theo trang". Tách ra để test được mà không
// phải dựng component.
//
// Một mục có dạng { key, nhan, phu?, mau?, nhom? }:
//   key   giá trị lưu (id mẫu, tiêu đề tag)
//   nhan  chữ chính (mã tắt, tên tag)
//   phu   chữ phụ để nhận ra mục (nội dung mẫu, mô tả tag)
//   nhom  id chủ đề; null = chưa có chủ đề
// ============================================================================

/**
 * Bỏ dấu tiếng Việt và viết thường. Người trực page hay gõ không dấu ("bao
 * hanh"), mã tắt lại hay có dấu ("5đàoSẹoLễ") — so khớp phải bỏ dấu cả hai
 * phía. `đ` không phải chữ có dấu kết hợp nên NFD không tách, phải đổi tay.
 */
export const boDau = s =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();

/**
 * Nội dung mẫu (markdown) → một dòng chữ thuần để hiện cạnh mã tắt và để tìm.
 *
 * KHÔNG dùng `getPlainText`: mỗi lần gọi nó dựng một MarkdownIt mới kèm plugin
 * rồi chạy DOMParser — 265 mẫu đo được ~0,85 s trong jsdom, mở bộ chọn là khựng.
 * Ở đây chỉ cần đọc được, không cần đúng từng quy tắc markdown: bỏ ký hiệu định
 * dạng, giữ chữ của link, gộp khoảng trắng.
 */
export const vanBanThuan = s =>
  String(s ?? '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // ảnh
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [chữ](link) -> chữ
    .replace(/<[^>]+>/g, ' ') // thẻ html sót
    .replace(/(\*\*|__|~~|`)/g, '')
    .replace(/(^|\s)[*_](\S)/g, '$1$2')
    .replace(/(\S)[*_](?=[\s.,:;!?)]|$)/g, '$1')
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Lọc theo cả chữ chính lẫn chữ phụ, khớp chuỗi con, không phân biệt dấu. */
export const locMuc = (muc, tuKhoa) => {
  const k = boDau(tuKhoa).trim();
  if (!k) return muc;
  return muc.filter(m => boDau(m.nhan).includes(k) || boDau(m.phu).includes(k));
};

/** So với danh sách đã lưu: bao nhiêu mục sẽ được thêm, bao nhiêu bị bỏ. */
export const demThayDoi = (goc, chon) => ({
  them: [...chon].filter(k => !goc.has(k)).length,
  bo: [...goc].filter(k => !chon.has(k)).length,
});

/**
 * Xếp mục vào nhóm theo đúng thứ tự `nhom`, mục không có (hoặc trỏ tới chủ đề
 * đã xoá) dồn vào nhóm cuối `khongNhom`. Nhóm rỗng bị bỏ — lọc xong mà vẫn hiện
 * 54 tiêu đề trống thì ô tìm kiếm mất tác dụng.
 */
export const nhomMuc = (muc, nhom, khongNhom) => {
  const theoId = new Map(nhom.map(n => [Number(n.id), { ...n, muc: [] }]));
  const le = { id: null, ten: khongNhom, mau: '', muc: [] };
  muc.forEach(m => {
    const n = m.nhom == null ? null : theoId.get(Number(m.nhom));
    (n || le).muc.push(m);
  });
  return [...theoId.values(), le].filter(n => n.muc.length);
};

/**
 * Nạp danh sách của trang khác, chỉ giữ những khoá còn tồn tại. Tag / mẫu đã
 * xoá vẫn có thể nằm trong cấu hình cũ — chép sang thì ô đếm "thêm N" báo sai
 * mà không có ô tick nào hiện ra cho người dùng thấy.
 */
export const chepTu = (nguon, muc) => {
  const con = new Set(muc.map(m => m.key));
  return new Set([...nguon].filter(k => con.has(k)));
};
